import * as React from "react";
import {useMemo, useState} from "react";
import {IUserPlanRow} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IProps {
    plan: IUserPlanRow;
}

const SortIcon: React.FC<{col: string; active: string; dir: SortDir}> = ({col, active, dir}) => {
    const visible = col === active;
    return (
        <i
            className={`fa-light fa-chevron-${dir === "asc" ? "up" : "down"} admin-analytics__sort-icon`}
            aria-hidden="true"
            style={{visibility: visible ? "visible" : "hidden"}}
        />
    );
};

export const UserPlanDrilldownBlock: React.FC<IProps> = ({plan}) => {
    const [sortState, setSortState] = useState<{col: string; dir: SortDir}>({col: "hits", dir: "desc"});

    const sortedPages = useMemo(
        () => applySort(plan.topPages, sortState.col, sortState.dir),
        [plan.topPages, sortState],
    );

    const sortTh = (col: string, label: string, extraClass?: string, defaultDir: SortDir = "desc") => {
        const isActive = sortState.col === col;
        const handleClick = () => setSortState({col, dir: isActive ? (sortState.dir === "desc" ? "asc" : "desc") : defaultDir});
        const handleKey = (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); }
        };
        return (
            <th
                className={`admin-analytics__th--sortable${extraClass ? ` ${extraClass}` : ""}`}
                onClick={handleClick}
                onKeyDown={handleKey}
                tabIndex={0}
                role="columnheader"
                aria-sort={isActive ? (sortState.dir === "asc" ? "ascending" : "descending") : undefined}
            >
                {label} <SortIcon col={col} active={sortState.col} dir={sortState.dir} />
            </th>
        );
    };

    return (
        <div className="admin-analytics__user-plan-block">
            <div className="admin-analytics__user-plan-header">
                <span className="admin-analytics__user-plan-name">{plan.planName}</span>
                <span className="admin-analytics__user-plan-hits">
                    {numberWithCommas(plan.totalHits)} page views
                </span>
            </div>
            <table className="admin-analytics__table admin-analytics__table--nested" style={{tableLayout: "fixed"}}>
                <colgroup>
                    <col />
                    <col style={{width: "80px"}} />
                </colgroup>
                <thead>
                    <tr>
                        {sortTh("page", "Top Page", undefined, "asc")}
                        {sortTh("hits", "Hits", "admin-analytics__cell--right")}
                    </tr>
                </thead>
                <tbody>
                    {sortedPages.map((p) => (
                        <tr key={p.page}>
                            <td>{p.page}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(p.hits)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
